import { createContext, useContext, useEffect, useMemo } from "react";
import en from "./en";
import romanizeAddress from "./address";

/**
 * 경로 접두사 기반 다국어.
 *   propmate.kr/        → 한국어 (basename "/")
 *   propmate.kr/en      → 영어   (basename "/en")
 *   propmate.kr/en/map  → 영어 지도
 *
 * BrowserRouter의 basename으로 접두사를 떼기 때문에 App.js의 라우트 테이블과
 * 컴포넌트 안의 <Link to="/map">, navigate("/login") 은 손대지 않아도
 * 자동으로 현재 언어의 경로를 유지한다.
 *
 * 사전은 "한국어 원문 → 영문" 매핑이다. 키가 없으면 한국어 원문을 그대로
 * 돌려주므로 번역이 빠져도 화면이 깨지지 않는다.
 */

export const LANGS = ["ko", "en"];
export const DEFAULT_LANG = "ko";
const DICTS = { en };

const PREFIX_RE = /^\/en(?=\/|$)/i;

/** 현재 pathname에서 언어와 BrowserRouter에 넘길 basename을 뽑는다. */
export function detectLang(pathname) {
    const matched = PREFIX_RE.exec(pathname || "");
    // basename은 실제로 매칭된 문자열을 그대로 써야 대소문자가 섞여도 접두사가 떨어진다.
    return matched
        ? { lang: "en", basename: matched[0] }
        : { lang: DEFAULT_LANG, basename: "/" };
}

/** 언어별 접두사. 한국어는 접두사가 없다. */
export function langPrefix(lang) {
    return lang === "en" ? "/en" : "";
}

/**
 * 라우터를 거치지 않는 이동(window.location 직접 대입 등)에 쓸 절대 경로.
 * langHref("/login", "en") → "/en/login"
 */
export function langHref(path, lang) {
    const clean = path.startsWith("/") ? path : `/${path}`;
    return `${langPrefix(lang)}${clean}` || "/";
}

// 개발 중 번역 누락을 한 번씩만 알려준다. 운영 빌드에서는 동작하지 않는다.
const warned = new Set();
function warnMissing(lang, text) {
    if (process.env.NODE_ENV !== "development") return;
    const key = `${lang}:${text}`;
    if (warned.has(key)) return;
    warned.add(key);
    console.warn(`[i18n] ${lang} 번역 누락: ${JSON.stringify(text)}`);
}

/** t("총 {n}건", { n: 3 }) 처럼 중괄호 자리표시자를 채운다. */
function interpolate(template, vars) {
    if (!vars) return template;
    return template.replace(/\{(\w+)\}/g, (whole, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
    );
}

export function translate(lang, text, vars) {
    if (typeof text !== "string") return text;
    const dict = DICTS[lang];
    if (!dict) return interpolate(text, vars);
    if (Object.prototype.hasOwnProperty.call(dict, text)) {
        return interpolate(dict[text], vars);
    }
    warnMissing(lang, text);
    return interpolate(text, vars);
}

/**
 * 훅을 쓸 수 없는 모듈(유틸·비(非)컴포넌트 함수)용 번역.
 * 언어는 페이지 로드 시점에 /en 접두사로 한 번 정해지고 그 뒤 바뀌지 않으므로
 * 모듈 로드 시 한 번 판정해 두어도 안전하다.
 */
export const currentLang =
    typeof window !== "undefined"
        ? detectLang(window.location.pathname).lang
        : DEFAULT_LANG;

/**
 * 화면에 뿌릴 주소. 영문일 때만 로마자 표기로 바꾼다.
 * 네이버 지오코더가 한국어 주소만 주기 때문에 필요하다.
 */
export function displayAddress(address) {
    return currentLang === "en" ? romanizeAddress(address) : address;
}

export function tStatic(text, vars) {
    return translate(currentLang, text, vars);
}

const I18nContext = createContext({
    lang: DEFAULT_LANG,
    t: (text, vars) => interpolate(text, vars),
    langHref: (path) => path,
});

// 영문 진입 시 교체할 문서 메타. index.html은 정적이라 런타임에 바꿔준다.
const DOC_META = {
    en: {
        title: "PROPMATE",
        description:
            "PROPMATE is a map-based platform that brings every piece of land information together — zoning, regulations, buildable scale and construction cost — in one place.",
    },
};

function setMeta(selector, value) {
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute("content", value);
}

export function I18nProvider({ lang = DEFAULT_LANG, children }) {
    const value = useMemo(
        () => ({
            lang,
            t: (text, vars) => translate(lang, text, vars),
            langHref: (path) => langHref(path, lang),
        }),
        [lang]
    );

    useEffect(() => {
        document.documentElement.lang = lang;
        const meta = DOC_META[lang];
        if (!meta) return; // 한국어는 index.html 원본 값을 그대로 둔다.
        document.title = meta.title;
        setMeta('meta[name="description"]', meta.description);
        setMeta('meta[property="og:description"]', meta.description);
        setMeta('meta[name="twitter:description"]', meta.description);
        setMeta('meta[property="og:locale"]', "en_US");
    }, [lang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    return useContext(I18nContext);
}

/** 가장 많이 쓰는 형태: const t = useT(); t("지도 보러가기") */
export function useT() {
    return useContext(I18nContext).t;
}
